(() => {
  const widgetSelector = '.jdgm-review-widget';
  const reviewSelector = '.jdgm-rev';
  const authorSelector = '.jdgm-rev__author';
  const timestampSelector = '.jdgm-rev__timestamp';
  const lowContextClass = 'jdgm-rev--low-context';
  const sortedAttribute = 'data-gd-review-quality-signature';
  const hiddenEmptyReviewAttribute = 'data-gd-hidden-empty-review';
  const widgetTimers = new WeakMap();
  const widgetObservers = new WeakMap();

  const normalizeText = (value) =>
    (value || '').replace(/[\u00A0\u200B-\u200F\uFEFF]/g, ' ').replace(/\s+/g, ' ').trim();

  const isGenericAuthor = (author) => {
    const normalized = normalizeText(author).toLowerCase();

    if (!normalized) return true;

    return (
      /^(user|customer|guest|shopper|anonymous)[\s._-]*\d{2,}$/i.test(normalized) ||
      /^user[\s._-]*[a-z0-9]{4,}$/i.test(normalized) ||
      /^(anonymous|verified buyer|shopper|guest)$/i.test(normalized)
    );
  };

  const getReviewYear = (timestamp) => {
    const normalized = normalizeText(timestamp);
    const yearMatch = normalized.match(/\b(20\d{2})\b/);

    if (yearMatch) return Number(yearMatch[1]);

    const parsed = Date.parse(normalized);
    if (!Number.isNaN(parsed)) return new Date(parsed).getFullYear();

    return null;
  };

  const isOlderReview = (timestamp) => {
    const year = getReviewYear(timestamp);

    return year !== null && year <= 2023;
  };

  const getReviewPriority = (review) => {
    const author = review.querySelector(authorSelector)?.textContent;
    const timestamp = review.querySelector(timestampSelector)?.textContent;

    return isGenericAuthor(author) || isOlderReview(timestamp) ? 1 : 0;
  };

  const hasMeaningfulContent = (review) => {
    const title = normalizeText(review.querySelector('.jdgm-rev__title')?.textContent);
    const body = normalizeText(
      review.querySelector('.jdgm-rev__body')?.textContent ||
        review.querySelector('.jdgm-rev__content')?.textContent
    );
    const media = review.querySelector(
      '.jdgm-rev__pics a[href], .jdgm-rev__pics a[data-mfp-src], .jdgm-rev__pics img, .jdgm-rev__vids video, .jdgm-rev__vids iframe, video, iframe'
    );

    return Boolean(title || body || media);
  };

  const hideEmptyReviews = (widget) => {
    widget.querySelectorAll(reviewSelector).forEach((review) => {
      const shouldHide = !hasMeaningfulContent(review);
      review.toggleAttribute(hiddenEmptyReviewAttribute, shouldHide);
      review.hidden = shouldHide;
    });
  };

  const getReviewParentGroups = (widget) => {
    const parents = new Set();

    widget.querySelectorAll(reviewSelector).forEach((review) => {
      if (review.parentElement) parents.add(review.parentElement);
    });

    return [...parents];
  };

  const sortReviewGroup = (parent) => {
    const reviews = [...parent.children].filter((child) => child.matches(reviewSelector));
    if (reviews.length < 2) return;

    const decoratedReviews = reviews.map((review, index) => ({
      review,
      index,
      priority: getReviewPriority(review),
    }));
    const signature = decoratedReviews
      .map(({ review, priority }) => `${priority}:${normalizeText(review.textContent).slice(0, 80)}`)
      .join('|');

    if (parent.getAttribute(sortedAttribute) === signature) return;

    decoratedReviews
      .sort((first, second) => first.priority - second.priority || first.index - second.index)
      .forEach(({ review, priority }) => {
        review.classList.toggle(lowContextClass, priority > 0);
        parent.appendChild(review);
      });

    parent.setAttribute(sortedAttribute, signature);
  };

  const sortWidgetReviews = (widget) => {
    hideEmptyReviews(widget);
    getReviewParentGroups(widget).forEach(sortReviewGroup);
  };

  const queueSort = (widget) => {
    window.clearTimeout(widgetTimers.get(widget));
    widgetTimers.set(widget, window.setTimeout(() => sortWidgetReviews(widget), 80));
  };

  const initWidget = (widget) => {
    queueSort(widget);

    if (widgetObservers.has(widget)) return;

    const observer = new MutationObserver(() => queueSort(widget));
    observer.observe(widget, { childList: true, subtree: true });
    widgetObservers.set(widget, observer);
  };

  const init = () => {
    document.querySelectorAll(widgetSelector).forEach(initWidget);
  };

  const pageObserver = new MutationObserver(init);
  pageObserver.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
